// pages/Checkout.jsx
//
// Description placement: product.description is fetched from the same
// GET /api/products/:id call as everything else on this page (no
// separate/duplicate fetch), and is rendered ONCE, below the
// quantity/price/total section - not on the product card (see
// ProductCard.jsx), so it never appears twice.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import OrderConfirmationModal from '../components/OrderConfirmationModal';
import { API_BASE } from '../config';

function Checkout() {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [settings, setSettings] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedTierLabel, setSelectedTierLabel] = useState('');
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [order, setOrder] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, settingsRes] = await Promise.all([
          fetch(`${API_BASE}/products/${productId}`),
          fetch(`${API_BASE}/settings`),
        ]);
        const productData = await productRes.json();
        setProduct(productData);
        setSettings(await settingsRes.json());

        // Default to the first (usually cheapest) tier so the dropdown
        // and price always start in sync
        if (productData.pricingTiers && productData.pricingTiers.length > 0) {
          setSelectedTierLabel(productData.pricingTiers[0].label);
        }
      } catch (err) {
        setError('Failed to load checkout page. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId]);

  // The actual unit price to charge, based on whichever tier is selected
  const unitPrice = (() => {
    if (!product) return 0;
    if (product.pricingTiers && product.pricingTiers.length > 0) {
      const tier = product.pricingTiers.find((t) => t.label === selectedTierLabel);
      return tier ? tier.price : product.pricingTiers[0].price;
    }
    return product.price;
  })();

  const handlePlaceOrder = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId, quantity, tierLabel: selectedTierLabel }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Something went wrong placing your order.');
        return;
      }
      setOrder(data);
      setOrderPlaced(true);
    } catch (err) {
      setError('Server error. Please try again.');
    }
  };

  if (loading) return <p className="page muted" style={{ textAlign: 'center' }}>Loading...</p>;
  if (error) return <p className="page" style={{ color: '#ff4757', textAlign: 'center' }}>{error}</p>;
  if (!product || !settings) return null;

  return (
    <div className="page-narrow panel">
      <h2 className="display" style={{ fontSize: '22px', marginTop: 0 }}>Checkout</h2>

      {!orderPlaced ? (
        <>
          <img src={product.icon} alt={product.name} style={{ width: '100%', borderRadius: '8px' }} />
          <h3 style={{ marginBottom: '12px' }}>{product.name}</h3>

          {product.pricingTiers && product.pricingTiers.length > 1 && (
            <>
              <label className="field-label">Validity</label>
              <select
                className="input"
                value={selectedTierLabel}
                onChange={(e) => setSelectedTierLabel(e.target.value)}
                style={{ marginBottom: '12px' }}
              >
                {product.pricingTiers.map((tier) => (
                  <option key={tier.label} value={tier.label}>
                    {tier.label} — ₹{tier.price}
                  </option>
                ))}
              </select>
            </>
          )}

          <p className="price-tag">₹{unitPrice} per unit</p>

          <label className="field-label">Quantity</label>
          <input
            type="number"
            min="1"
            max={product.quantity}
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            className="input"
            style={{ maxWidth: '100px', marginBottom: '16px' }}
          />

          <h3 style={{ marginBottom: '16px' }}>Total: <span className="price-tag">₹{unitPrice * quantity}</span></h3>

          {/* Description shows here - below quantity/price/total, and
              ONLY on this page (not on the ProductCard grid). This is
              the single place it's rendered, sourced directly from
              product.description fetched above - no duplication. */}
          {product.description && (
            <div style={{ borderTop: '1px solid var(--panel-border)', paddingTop: '14px', marginBottom: '18px' }}>
              <p className="field-label" style={{ marginBottom: '6px' }}>Description</p>
              <p className="muted" style={{ fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                {product.description}
              </p>
            </div>
          )}

          <button onClick={handlePlaceOrder} className="btn-primary" style={{ width: '100%' }}>
            Place Order
          </button>
        </>
      ) : (
        <>
          <h3 style={{ textAlign: 'center' }}>Scan & Pay</h3>
          <img src={settings.qrCodeImage} alt="Payment QR Code" style={{ width: '220px', margin: '0 auto', display: 'block', borderRadius: '8px' }} />
          <p style={{ textAlign: 'center', marginTop: '14px' }}>
            Amount to pay: <span className="price-tag">₹{order.totalPrice}</span>
          </p>
          <p className="muted" style={{ textAlign: 'center', fontSize: '13px' }}>
            After paying, take a screenshot of the payment confirmation, then tap below.
          </p>

          <button onClick={() => setShowConfirmation(true)} className="btn-whatsapp" style={{ width: '100%', marginTop: '10px' }}>
            I've Paid — Continue
          </button>
        </>
      )}

      {showConfirmation && (
        <OrderConfirmationModal
          order={order}
          whatsappNumber={settings.whatsappNumber}
          onClose={() => setShowConfirmation(false)}
        />
      )}
    </div>
  );
}

export default Checkout;
